/**
 * Centralised route paths for the web app.
 *
 * Org-scoped routes live under the `/orgs/:orgSlug` namespace (built by
 * `ROUTES.org(slug)` / {@link orgBasePath}) because the URL is the source
 * of truth for the active organization (see `OrgSlugResolver`). The fixed
 * `orgs` prefix means a top-level feature route can never collide with an
 * organization's slug. Use the `useOrgRoutes()` hook in components to
 * avoid threading the slug through manually.
 *
 * Pre-`/orgs` URL shapes keep working: the web app forwards `/<slug>/...`
 * to `/orgs/<slug>/...` (`LegacyOrgPathRedirect`) and slug-less paths to
 * `/orgs/<activeSlug>/...` ({@link LEGACY_ROOT_REDIRECT_PATHS}).
 *
 * Root-only routes (admin, profile, settings) stay at the top level — they
 * are not org-scoped.
 */

/** First path segment of the org-scoped URL namespace (`/orgs/:orgSlug/...`). */
export const ORG_PATH_PREFIX = 'orgs';

/** Base path for an org's URL subtree under the `orgs` namespace: `/orgs/<slug>`. */
export function orgBasePath(slug: string): string {
  return `/${ORG_PATH_PREFIX}/${slug}`;
}

export const ROUTES = {
  root: '/',
  /**
   * Stable alias for the marketing home page. `/` only renders it for
   * anonymous visitors — a signed-in user is redirected into their active
   * organization — so `/home` is the URL that always lands on the
   * marketing page, whatever the session state.
   */
  home: '/home',
  onboard: '/onboard',
  profile: '/profile',
  userProfile: (id: string) => `/profile/${id}`,
  settings: '/settings',
  credentials: '/credentials',
  userSecrets: '/me/secrets',
  org: (slug: string) => {
    const base = orgBasePath(slug);
    return {
      home: base,
      inbox: `${base}/inbox`,
      runs: `${base}/runs`,
      agent: (id: string) => `${base}/agents/${id}`,
      flowRun: (id: string) => `${base}/automation-runs/${id}`,
      flowRunNode: (id: string, nodeId: string) =>
        `${base}/automation-runs/${id}?nodeId=${encodeURIComponent(nodeId)}`,
      blueprints: `${base}/configure/build/environments`,
      blueprintNew: `${base}/configure/build/environments/new`,
      blueprint: (id: string) => `${base}/configure/build/environments/${id}`,
      baseImage: `${base}/configure/build/environments/base-image`,
      integrations: `${base}/configure/connect/integrations`,
      integration: (provider: string) => `${base}/configure/connect/plugins/${provider}`,
      mcpHub: `${base}/configure/connect/mcp`,
      mcpHubSection: (section: string) => `${base}/configure/connect/mcp/${section}`,
      mcpSetup: `${base}/configure/connect/mcp/setup`,
      credentialsHub: `${base}/configure/connect/credentials`,
      credentialsHubSection: (scope: string, section: string) =>
        `${base}/configure/connect/credentials/${scope}/${section}`,
      credentialsHubTeam: (teamId: string, section: string) =>
        `${base}/configure/connect/credentials/team/${teamId}/${section}`,
      sharingHub: `${base}/configure/connect/sharing`,
      sharingHubScope: (scope: string) => `${base}/configure/connect/sharing/${scope}`,
      chat: `${base}/chat`,
      // Automations surface (product name). Legacy `/flows` URLs redirect here.
      automations: `${base}/automations`,
      /** Cross-automation runs list inside the Automations takeover (Live). */
      automationsRuns: `${base}/automations/runs`,
      /** Cross-automation runs history inside the Automations takeover. */
      automationsRunsHistory: `${base}/automations/runs/history`,
      /** @deprecated Use `automations`. Kept as an alias during the FE rename. */
      flows: `${base}/automations`,
      agentPersonas: `${base}/configure/build/agents/personas`,
      // Agents hub (Personas + Agent Models). Index redirects to Personas.
      agentsHub: `${base}/configure/build/agents`,
      agentsHubSection: (section: string) => `${base}/configure/build/agents/${section}`,
      misc: `${base}/misc`,
      plugins: `${base}/plugins`,
      orgPlugins: `${base}/configure/connect/integrations`,
      plugin: (name: string) => `${base}/configure/connect/plugins/${name}`,
      // Company-level configuration: what the company owning this
      // organization carries (plan, the organizations it owns, and — as they
      // move here — its identity trust and billing). The URL stays
      // org-scoped on purpose: the active organization is the door the
      // server authorizes through (`routes.billingEntity`), and any
      // organization in the company is a valid one, so the org in the URL is
      // exactly the org the API call is made against. A `/companies/:slug`
      // namespace would have to pick a door implicitly.
      company: `${base}/configure/company`,
      /**
       * Company → Security: the company's identity provider connection and
       * the applications its assertions may reach. Contributed by
       * `plugin-auth-oidc`, so the page exists only where enterprise sign-in
       * is composed; the constant lives here so the host can link to it and
       * redirect {@link LEGACY_ENTERPRISE_AUTHORIZATION_PATH} at it.
       */
      companySecurity: `${base}/${COMPANY_SECURITY_PATH}`,
      orgRoot: `${base}/org`,
      settings: `${base}/configure/organization/general`,
      // Organization hub (General settings + Service accounts).
      organizationHub: `${base}/configure/organization`,
      organizationHubSection: (section: string) => `${base}/configure/organization/${section}`,
      // Organization-tier Network Policies page. Contributed by the
      // network-policy plugin, so the page and its nav entry exist only for
      // orgs with the plugin installed; the constant lives here so host and
      // plugin surfaces can link to it without hand-built paths.
      security: `${base}/configure/network-policies`,
      members: `${base}/configure/people/members`,
      teams: `${base}/configure/people/teams`,
      team: (id: string) => `${base}/configure/people/teams/${id}`,
      teamSecrets: (id: string) => `${base}/configure/people/teams/${id}/secrets`,
      teamModelProviders: (id: string) => `${base}/configure/people/teams/${id}/model-providers`,
      roles: `${base}/configure/people/roles`,
      role: (id: string) => `${base}/configure/people/roles/${id}`,
      // Org-level service accounts: machine identities managed at the org
      // level, each with its own API keys. Backed by the `/service-accounts`
      // API. The detail page (`serviceAccount(id)`) is where keys are minted.
      serviceAccounts: `${base}/configure/organization/service-accounts`,
      serviceAccount: (id: string) => `${base}/configure/organization/service-accounts/${id}`,
      secrets: `${base}/configure/connect/credentials/organization/secrets`,
      secretsStorage: `${base}/configure/connect/credentials/organization/secrets/storage`,
      agentModels: `${base}/configure/build/agents/models`,
      orgModelProviders: `${base}/configure/connect/credentials/organization/model-providers`,
      // Security drill — surfaces every model secret the signed-in user
      // can manage at any tier. The personal page is the entry point;
      // it links out to the team / org pages for keys they can manage.
      securityRoot: `${base}/security`,
      modelProvidersPersonal: `${base}/configure/connect/credentials/personal/model-providers`,
      // Personal user-tier key/value secrets, surfaced under Security.
      // Backed by the `/me/secrets` API; the legacy root-level
      // `ROUTES.userSecrets` URL redirects here.
      securitySecrets: `${base}/configure/connect/credentials/personal/secrets`,
      // Personal API keys for the public Reflex API + SDK, surfaced under
      // Security. Backed by the `/me/api-keys` API.
      securityApiKeys: `${base}/configure/connect/credentials/personal/api-keys`,
      // Sharing grants: who can reach the caller's sessions and for how
      // long. Backed by the `/resource-grants` API.
      securitySharing: `${base}/configure/connect/sharing/personal`,
      // Org-wide session sharing policy (which access levels a share may
      // hand out). Lives in the Usage and Costs nav group; edits are gated
      // on `org:manage`.
      sharingSettings: `${base}/configure/usage/sharing`,
      // Per-org bootstrap wizard. Mounted as a sibling of the main org
      // subtree (its own shell, no app chrome) but org-scoped all the same.
      setup: `${base}/setup`,
      setupStage: (stage: string) => `${base}/setup/${stage}`,
    };
  },
  /**
   * Root-level deep link that activates an in-editor tutorial by name. The
   * tutorial plugin's launcher route mounts at this URL, starts the matching
   * script, and navigates to the tutorial's first step.
   */
  tutorial: (name: string) => `/tutorial/${name}`,
  admin: {
    root: '/admin',
    monitor: '/admin/monitor',
    monitorOrg: (id: string) => `/admin/monitor/orgs/${id}`,
    users: '/admin/users',
    user: (id: string) => `/admin/users/${id}`,
    organizations: '/admin/organizations',
    organization: (id: string) => `/admin/organizations/${id}`,
    companies: '/admin/companies',
    company: (id: string) => `/admin/companies/${id}`,
    settings: '/admin/settings',
    email: '/admin/email',
    appearance: '/admin/appearance',
    maintenance: '/admin/maintenance',
    flags: '/admin/flags',
    auth: '/admin/auth',
    serviceSecrets: '/admin/service-secrets',
    webhooks: '/admin/webhooks',
    plugins: '/admin/plugins',
    plugin: (name: string) => `/admin/plugins/${name}`,
    pluginSetup: (name: string) => `/admin/plugins/${name}/setup`,
    database: '/admin/database',
    seeds: '/admin/seeds',
    notifications: '/admin/notifications',
  },
} as const;

/**
 * Integration id used on Admin → Service Secrets for a plugin's
 * service-tier secret group (`plugin-<name>-service`).
 */
export function pluginServiceIntegrationId(pluginName: string): string {
  return `plugin-${pluginName}-service`;
}

/**
 * Deep link to Admin → Service Secrets focused on a plugin's service-tier
 * group. Used from admin plugin setup/detail so operators can set missing
 * OAuth credentials without hunting through the full secrets page.
 */
export function pluginServiceSecretsHref(pluginName: string): string {
  const integration = pluginServiceIntegrationId(pluginName);
  return `${ROUTES.admin.serviceSecrets}?integration=${encodeURIComponent(integration)}`;
}

/**
 * Root-level route patterns from before the org-slug URL migration. The
 * web app mounts each one as a `LegacyRedirect` route that forwards to
 * `/orgs/:activeSlug/<path>`, keeping old bookmarks and server-issued
 * links (e.g. Slack notification URLs like `/agents/:agentId`) working.
 *
 * Every entry also feeds {@link RESERVED_ROOT_SEGMENTS}: these paths can
 * never be org URLs, so their first segments are never org slugs.
 */
export const LEGACY_ROOT_REDIRECT_PATHS = [
  'inbox',
  'runs',
  'agents/:agentId',
  'agent-types',
  'agent-types/:typeId',
  'blueprints',
  'blueprints/new',
  'blueprints/:blueprintId',
  'flows',
  'automations',
  'agent-personas',
  'personas',
  'integrations',
  'integrations/mcp-setup',
  'integrations/slack',
  'org',
  'org/settings',
  'org/members',
  'org/teams',
  'org/teams/:teamId',
  'org/roles',
  'org/roles/:roleId',
  'org/service-accounts',
  'org/service-accounts/:id',
  'org/secrets',
  'org/secrets/storage',
  'org/model-providers',
  'org/agent-models',
  'org/integrations',
  'org/integrations/:provider',
  'org/billing',
  'org/billing/usage',
  'org/costs',
  'org/costs/users/:userId',
  'org/costs/keys/:secretId',
  'org/costs/keys/:secretId/records',
  'org/costs/keys/:secretId/records/:category',
  'org/costs/providers/:provider',
  'org/costs/categories/:category',
  'org/security',
  'security',
  'security/model-providers',
  'security/secrets',
  'security/api-keys',
  'security/sharing',
  'misc',
  'plugins',
  'admin/agent-models',
  'integrations/linear',
  'integrations/jira',
  'integrations/google-workspace',
  'mcp-servers',
  'ai-gateways',
  'tailscale',
  'agent-personas/new',
  'agent-personas/:id',
  'agent-personas/:id/edit',
] as const;

/**
 * Where Company → Security lived before the company URL namespace existed.
 *
 * The page's settings were always the company's, but it had no company path
 * to sit on, so it was contributed under `/configure/organization/...` and
 * only the nav mapping said otherwise. Now that `ROUTES.org(slug).company`
 * exists the URL says it too, and this is the one org-relative path the web
 * app redirects at {@link ROUTES}`.org(slug).companySecurity`, so links
 * issued before the move keep working.
 */
export const LEGACY_ENTERPRISE_AUTHORIZATION_PATH =
  'configure/organization/enterprise-authorization';

/**
 * Company → Security, org-relative — what
 * {@link ROUTES}`.org(slug).companySecurity` prefixes an org onto, and the
 * form the host's own route table needs, which has no org to prefix.
 *
 * The page itself is contributed by `plugin-auth-oidc`, which declares the
 * absolute form (`/configure/company/security`) in its web manifest. The host
 * matches on this constant to notice when that plugin is *not* composed and
 * nothing is serving the path, so the two spellings have to agree.
 */
export const COMPANY_SECURITY_PATH = 'configure/company/security';

/**
 * First path segments that can never be an organization slug.
 *
 * Canonical org URLs live under `/orgs/...`, so they cannot collide with
 * root-level routes. This set protects the *compat* surfaces that keep
 * pre-`/orgs` URLs working: {@link orgSlugFromPathname} must not read a
 * root-level route's first segment as an org slug, and the web app's
 * `LegacyOrgPathRedirect` must not forward reserved paths into
 * `/orgs/...`. The server also refuses these as slugs at org
 * create/rename so an org's pre-`/orgs` URLs are never shadowed by a
 * root route.
 *
 * Any route mounted outside `/orgs/:orgSlug` (`rootLevel`, `public`, or
 * `chromeless`) must have its first segment listed here — enforced for
 * composed plugin routes by the runloop-flow audit test. Once compat
 * parsing of pre-`/orgs` URLs is retired (see
 * {@link orgSlugFromPathname}), this set freezes: new root routes will
 * no longer need an entry.
 */
export const RESERVED_ROOT_SEGMENTS: ReadonlySet<string> = new Set([
  // The org URL namespace itself
  ORG_PATH_PREFIX,
  // Root-level (non-org-scoped) app routes
  'admin',
  'home',
  'onboard',
  'profile',
  'settings',
  'credentials',
  'me',
  'org-setup',
  // Auth/session surfaces
  'login',
  'signup',
  'logout',
  'invites',
  'oauth',
  // Server-owned path namespaces on the same origin
  'api',
  'health',
  'ws',
  'webhooks',
  // Public marketing/docs surfaces and root-level plugin routes
  'docs',
  'demos',
  'pages',
  'changelog',
  'tutorial',
  'tutorials',
  'waitlist',
  'replays',
  // Referral short links (`/i/<code>`) and the page behind them
  // (plugin-referrals)
  'i',
  'referrals',
  // Legacy pre-org-slug routes (each mounted as a LegacyRedirect)
  ...LEGACY_ROOT_REDIRECT_PATHS.map((p) => p.split('/')[0]),
]);

/**
 * First path segments of the org-scoped routes (the children of
 * `/orgs/:orgSlug`), derived from {@link ROUTES.org}. Renderers that
 * org-scope author-written bare paths (e.g. docs CTA links like `/runs`)
 * use this as a positive classifier: a bare internal path is an org page
 * iff its first segment is in this set.
 */
export const ORG_ROUTE_FIRST_SEGMENTS: ReadonlySet<string> = new Set(
  Object.values(ROUTES.org('_'))
    .map((entry: string | ((...args: string[]) => string)) =>
      typeof entry === 'function' ? entry('_', '_') : entry,
    )
    // Paths look like `/orgs/_/<first>/...`; `home` (`/orgs/_`) has no
    // child segment and drops out via the filter.
    .map((path) => path.split('/')[3] ?? '')
    .filter(Boolean),
);

/**
 * A URL segment that names a row rather than a page.
 *
 * Our own ids (`coreIds.generate` / plugin `createIdFactory`) and the
 * provider ids we put in URLs (Runloop blueprints `bpt_…`, snapshots
 * `snp_…`, devboxes `dbx_…`) share the `<prefix>_<base62>` shape but not
 * the body length, so the strict minted-id contract regex
 * (`ENTITY_ID_RE`, 22 chars exactly) misses every provider id. This is the
 * route-scoped predicate: deliberately looser, because the cost of a miss
 * (an id carried into an org that does not own it) is worse than the cost
 * of a false positive (landing one level higher up the same subtree).
 *
 * False positives are structurally impossible today: no static route
 * segment in the host table or in any plugin web manifest contains an
 * underscore — every one of them is lowercase kebab-case. Keep it that
 * way; a static segment shaped `ab_XXXXXXXX` would start being truncated.
 */
export const RESOURCE_ID_SEGMENT_RE = /^[a-z]{2,4}_[A-Za-z0-9]{8,}$/;

/**
 * Org-relative paths (`/runs`, `/chat`, `''` for the org home) of every
 * {@link ROUTES.org} entry that takes no parameters — the pages that mean
 * the same thing in any organization, because nothing in the URL below the
 * slug names an org-owned row.
 *
 * Used when a path has to be carried across an org boundary (the org
 * switcher): a parameterised route's id belongs to the org it came from,
 * so the switch falls back to the nearest paramless ancestor in this set.
 *
 * Host routes only. Plugin-contributed pages declare their paths in their
 * own web manifests and never reach `ROUTES`, so a plugin's paramless list
 * page (`/projects`, `/axons`, `/notifications`, `/replays`,
 * `/automation-replays`, …) is *not* in this set and is never chosen as a
 * truncation target — a switch from `/projects/prj_<id>` lands on the new
 * org's home instead of its project list. That is a worse landing, not a
 * wrong one, and it quietly worsens as plugins add pages; closing it means
 * plumbing the composed plugin route table in here.
 */
export const ORG_PARAMLESS_PATHS: ReadonlySet<string> = new Set(
  Object.values(ROUTES.org('_'))
    .filter((entry): entry is string => typeof entry === 'string')
    // Every `ROUTES.org` entry is an absolute URL under the sentinel org, and
    // the slice below assumes it. Filter rather than trust: an absolute
    // non-org entry added later would otherwise be prefix-stripped into a
    // garbage path that the org switcher would happily route to.
    .filter((path) => path.startsWith(orgBasePath('_')))
    // `/orgs/_/runs` -> `/runs`; the org home (`/orgs/_`) -> `''`.
    .map((path) => path.slice(orgBasePath('_').length)),
);

/**
 * Extract the org slug from a URL pathname, or `null` when the path is
 * not org-scoped. Understands both URL shapes:
 *
 * - Canonical: `/orgs/<slug>/...` → the second segment, verbatim. No
 *   reserved-set filtering applies under the prefix — an unknown or
 *   reserved-looking value is just an unresolvable slug, which the server
 *   ignores when asserted via `x-organization-id` (see
 *   `auth-middleware.ts#resolveActiveOrganization`).
 * - Compat (pre-`/orgs` URLs): `/<slug>/...` → the first segment when it
 *   is not in {@link RESERVED_ROOT_SEGMENTS}. This branch keeps old
 *   bookmarks and already-sent links scoped to the right org on hard
 *   loads, before the client-side redirect to `/orgs/...` commits. It is
 *   intended to be removed once pre-`/orgs` URLs no longer need parsing;
 *   {@link RESERVED_ROOT_SEGMENTS} freezes at that point.
 */
export function orgSlugFromPathname(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  if (!first) return null;
  if (first === ORG_PATH_PREFIX) return segments[1] || null;
  if (RESERVED_ROOT_SEGMENTS.has(first)) return null;
  return first;
}

/**
 * Strips a leading `/` from a path so it can be mounted as a child of a
 * parent `Route` (React Router treats child paths as relative).
 */
export function asRelativePath(path: string): string {
  return path.replace(/^\//, '');
}

/**
 * Prefix an absolute path with an org's base path. Used to convert
 * plugin-declared paths (`/chat`) into org-scoped URLs (`/orgs/acme/chat`)
 * when rendering nav items, command palette entries, etc.
 */
export function withOrgSlug(slug: string, path: string): string {
  if (!slug) return path;
  if (!path.startsWith('/')) return `${orgBasePath(slug)}/${path}`;
  return `${orgBasePath(slug)}${path}`;
}
